import { Box } from "@mui/material";
import React, { useState, useMemo } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import errorIcon from "../../assets/icons/error-icon.svg";
import BackArrow from "../../assets/icons/left-pointing-arrow-pink.svg";
import {
  useCampaignsHistory,
  useCampaignExecutionLog,
  useGetCampaignDetail,
  useSegmentList,
  useUpdateCampaignEndDate,
} from "../../services/campaign/index";
import { useAppMe } from "../../services/okta";
import DatasetDropdown from "../../shared/components/dataset-dropdown/DatasetDropdown";
import { useStickyHeader } from "../../shared/hooks/StickyHeader";
import formatValuesCampaign from "../../utils/formatValuesCampaign";
import { BorderSecondary, CircularProgressCustom, PrimaryButton } from "../../utils/muiCustomTheme";
import { CreateCampaignProps } from "../campaigns/campaign-interface";
import InsightsWrap from "../createSegment/InsightsWrap";
import DialogDeleteComponent from "../segmentSummary/DialogDeleteComponent";
import DialogEditComponent from "../segmentSummary/DialogEditSegment";
import CampaignFormComponent from "./CampaignFormComponent";
import "./CreateCampaigns.scss";
import { useCountryList } from "../../services/okta";
import dayjs from "dayjs";
import EditHistoryTable from "./EditHistoryTable";
import ExecutionLogTable from "./ExecutionLogTable";
import ErrorPanel from "../../shared/components/error-panel/ErrorPanel";
import { useSimulationParams } from "../../shared/hooks/useSimulationParams";

const SummaryCampaign: React.FC<CreateCampaignProps> = () => {
  const { campaignId } = useParams();

  const navigate = useNavigate();
  const { headerRef, sentinelRef, isSticky } = useStickyHeader();
  const { createLink } = useSimulationParams();
  const [showDeleteSegmentDialog, setShowDeleteSegmentDialog] = useState(false);

  const { data: dataAppMe, isPending: isPendingAppMe } = useAppMe({});
  const sub = dataAppMe?.data?.user_id;

  const closeDeleteSegmentDialog = () => {
    setShowDeleteSegmentDialog(false);
  };

  const handleDeleteSegment = () => {
    if (!sub || !campaignName) return;

    deleteCampaign({
      campaignId, user: sub,
      campaignName
    });

    setShowDeleteSegmentDialog(false);
  };

  const [showEditSegmentDialog, setShowEditSegmentDialog] = useState(false);

  const closeEditSegmentDialog = () => {
    setShowEditSegmentDialog(false);
  };

  const navigateOnCampaign = () => {
    navigate(createLink(`/create/campaigns?id=${campaignId}`));
  };

  const { data: countries, isPending: isPendingCountries} = useCountryList();
  const countryTags = Array.from(new Set(countries?.flatMap((c) => c.tags) || []));

  const {
    data: dataCampaignDetail,
    isPending: isPendingDataCampaignDetail,
    isFetching: isFetchingDataCampaignDetail,
    error: campaignError,
  } = useGetCampaignDetail({
    query: {
      campaignId,
      tags:countryTags,
      countries: countries?.map((c)=>c.name),
    },
    options: {
      enabled: !!campaignId && !isPendingCountries && countryTags.length > 0,
    },
  });

  const parseDataCampaignDetail = JSON.parse(dataCampaignDetail?.data || "[]");

  const { data: campaignsHistoryData, isPending: isPendingCampaignsHistory } = useCampaignsHistory({
    query: { campaignId },
    options: { enabled: !!campaignId && !isPendingDataCampaignDetail && parseDataCampaignDetail.length > 0 },
  });

  const { data: executionLogData, isPending: isPendingExecutionLog } = useCampaignExecutionLog({
    query: { campaignId },
    options: { enabled: !!campaignId && !isPendingDataCampaignDetail && parseDataCampaignDetail.length > 0 },
  });

  const {
    campaignName,
    country,
    count = 0,
    engaged = 0,
    reachable = 0,
    uniqueEmails = 0,
    brand,
    folder,
    segments_exc,
    segments_inc,
    build,
  } = parseDataCampaignDetail?.[0] || {};


  const adminValue = useMemo(() => {
    if (!countries?.length || !country) return 0;

    const normalizedCountry = country.trim().toLowerCase();
    const matchedCountry = countries.find(
      (c) =>
        c.name.toLowerCase() === normalizedCountry || // match by name
        c.code.toLowerCase() === normalizedCountry.slice(0, 2) // match by first 2 letters of code
    );

    return matchedCountry ? matchedCountry.admin : 0;
  }, [countries, country]);

  let buildParse: any = {};
  try {
    buildParse = build ? JSON.parse(build) : {};
  } catch {
    buildParse = {};
  }

  const {
    audienceGroups,
    campaignType,
    endDate,
    quantityValue,
    startDate,
    timeValue,
    deliveryType,
  } = buildParse || {};

  let convertedTimeStart;
  let convertedTimeEnd;
  let convertedStartDate;
  let convertedEndDate;
  let convertedTS;
  let utcTimeStart;
  let utcTimeEnd;

  if (timeValue && startDate){
    utcTimeStart=dayjs.utc(`${timeValue.replace("T","")}`)
    convertedTimeStart=utcTimeStart.tz(dayjs.tz.guess())
    convertedStartDate=convertedTimeStart.format("YYYY-MM-DD")
    convertedTS=convertedTimeStart.format("HH:mm:ss")
  } else {
    convertedStartDate=null
    convertedTS=null
  }

  if(endDate){
    utcTimeEnd=dayjs.utc(`${endDate}${timeValue.split("T")[1]}`)
    convertedTimeEnd=utcTimeEnd.tz(dayjs.tz.guess())
    convertedEndDate=convertedTimeEnd.format("YYYY-MM-DD")
  } else{
    convertedEndDate=null
  }

  const tempVal = formatValuesCampaign({
    user: sub,
    campaignName: campaignName || "",
    quantityValue,
    campaignType,
    deliveryType,
    country: country ,
    brand: brand ,
    folder: folder ,
    afterSpecificNumberOfOccurrences: quantityValue>0?true:false,
    include: segments_inc?segments_inc.map((i:any)=>i.segmentId) : [],
    exclude: segments_exc?segments_exc.map((i:any)=>i.segmentId) : [],
    audienceGroups,
    timeValue: convertedTS,
    startDate:convertedStartDate,
    endDate:convertedEndDate,
  });

  const campaignRefreshData = useMemo(() => ({
    campaignId,
    campaignName,
    campaignType,
    include: segments_inc || [],
    exclude: segments_exc || [],
    audienceGroups: audienceGroups || [],
    format: "JSON",
    quantityValue: quantityValue || 0,
    country,
    folder,
    brand,
    path: [country, brand, folder].filter(Boolean).join("/"),
    afterSpecificNumberOfOccurrences: quantityValue > 0,
    timeValue,
    endDate,
    startDate,
    user: sub,
    type: "check",
  }), [audienceGroups, brand, campaignId, campaignName, campaignType, country, endDate, folder, quantityValue, segments_exc, segments_inc, startDate, sub, timeValue]);

  const methods = useForm<any>({
    defaultValues: tempVal,
    values: tempVal,
  });

  const { control, getValues } = methods;
  const selectedCountry = getValues("country");

  const { data: dataSegment, isPending: isPendingDataSegment } = useSegmentList({
    query: { countryName: selectedCountry, tags: countryTags },
    options: {
      enabled:  !!selectedCountry && countryTags.length > 0
    },
  });

  const { mutate: deleteCampaign, isPending: isPendingDeleteCampaign } = useUpdateCampaignEndDate({
    user: sub,
    campaignName,
    options: {
      onSuccess: (res: any) => {
        if (res) {
          navigate(createLink("/campaigns"));
        }
      },
      onError: (err: any) => {
        setShowDeleteSegmentDialog(false);
      },
    },
  });

  const navigateOnCampaigns = () => {
    navigate(createLink("/campaigns"));
  };
  if (
    isPendingAppMe ||
    isPendingCountries ||
    isPendingDataCampaignDetail
  ) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <CircularProgressCustom />
      </Box>
    );
  }

  if (campaignError) {
    const status = (campaignError as any)?.response?.status || (campaignError as any)?.status;
    const message = (campaignError as any)?.response?.data?.message;
    return (
      <ErrorPanel
        code={status}
        message={message}
        primaryLabel="Back to Campaigns"
        onPrimary={navigateOnCampaigns}
      />
    );
  }

  return (
    <>
    <FormProvider {...methods}>
      <form>
        <div className="create-campaign">
          <div ref={sentinelRef}></div>

          <div ref={headerRef} className={`create-campaign__wrap ${isSticky ? "is-sticky" : ""}`}>
            <div className="create-campaign__wrap__heading-wrap" onClick={navigateOnCampaigns}>
              <div className="create-campaign__wrap__heading-wrap__icon">
                <img src={BackArrow} alt="Back" />
              </div>
              <div className="create-campaign__wrap__heading-wrap__heading">Back to Campaign</div>
            </div>

            <div className="create-campaign__wrap__btn-wrap">
              <div className="create-campaign__wrap__btn-wrap__label">
                {getValues("country") || "Unkown Country"} |{" "}
                {getValues("campaignName") || "Unkown Campaign"}
              </div>
              <div className="create-campaign__wrap__btn">
                {adminValue === 1 && (
                  <>
                    <BorderSecondary
                      type="button"
                      onClick={() => !isPendingDeleteCampaign && setShowDeleteSegmentDialog(true)}
                    >
                      {isPendingDeleteCampaign ? "Deleting..." : "Delete"}
                    </BorderSecondary>

                    <PrimaryButton
                      variant="contained"
                      onClick={() => setShowEditSegmentDialog(true)}
                    >
                      Edit
                    </PrimaryButton>
                  </>
                )}
              </div>
            </div>

            <div className="create-campaign__dataset-wrapper">
              <div className="home__dataset-sec">
                <Controller
                  control={control}
                  name="country"
                  rules={{ required: "Please provide dataset" }}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <div ref={field.ref} className="view-all-attributes__dataset-wrapper__label">
                        Market:
                      </div>
                      <DatasetDropdown
                        value={field.value}
                        disabled={true}
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                      />
                      {error?.message && (
                        <div className="create-campaign__input-wrap__segment-err">
                          <img src={errorIcon} alt="Error Icon" />
                          {error.message}
                        </div>
                      )}
                    </>
                  )}
                />
              </div>
            </div>
            <div className="create-campaign__header-divider"></div>
          </div>

          <InsightsWrap
            campaignRefreshData={campaignRefreshData}
            insights={[
              {
                key: "uniqueConsumers",
                label: "Unique Consumers",
                value: count,
                requiresRefresh: false,
              },
              {
                key: "engagedConsumers",
                label: "Engaged Consumers",
                value: engaged,
                requiresRefresh: false,
              },
              {
                key: "reachableConsumers",
                label: "Reachable Consumers",
                value: reachable,
                requiresRefresh: false,
              },
              {
                key: "uniqueEmails",
                label: "Unique Emails",
                value: uniqueEmails,
                requiresRefresh: false,
              },
            ]}
            title="Campaign insights:"
          />

          <div className="create-campaign__divider"></div>

          <div className="disabled">
            <CampaignFormComponent dataSegment={dataSegment} countryTags={countryTags} />
          </div>
        </div>

        <DialogEditComponent
          open={showEditSegmentDialog}
          onClose={closeEditSegmentDialog}
          onEdit={navigateOnCampaign}
          type="campaign"
        />

        <DialogDeleteComponent
          open={showDeleteSegmentDialog}
          onClose={closeDeleteSegmentDialog}
          onDelete={handleDeleteSegment}
          type="campaign"
        />
      </form>
    </FormProvider>
    <div className="create-campaign__history-wrap">
      <div className="create-segment__divider"></div>
      <div className="create-segment__insights-wrap__head mb-3">Edit History:</div>
      <EditHistoryTable data={campaignsHistoryData?.data || campaignsHistoryData} isLoading={isPendingCampaignsHistory} />
    </div>
    <div className="create-campaign__history-wrap">
      <div className="create-segment__divider"></div>
      <div className="create-segment__insights-wrap__head mb-3">Execution Log:</div>
      <ExecutionLogTable data={executionLogData?.data || executionLogData} isLoading={isPendingExecutionLog} />
    </div>
    </>
  );
};

export default SummaryCampaign;
